import * as d3 from 'd3';

import mapSettings from './mapSettings';

// Global map settings.
const { mapWidth, mapScaleMin, mapScaleMax, transitionDuration } = mapSettings;

/**
 * Zoom in.
 *
 * @param {Object} ref  - SVG ref to zoom in on.
 * @param {Object} zoom - d3 zoom object.
 */
export function zoomIn( ref, zoom ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );
	const transform = d3.zoomTransform( svg.node() );
	const scale = transform.k;

	// Return early if fully zoomed in.
	if ( scale >= mapScaleMax ) {
		return;
	}

	svg
		.transition()
		.duration( transitionDuration )
		.call( zoom.scaleTo, scale * 2 );
}

/**
 * Zoom out.
 *
 * @param {Object} ref  - SVG ref to zoom out on.
 * @param {Object} zoom - d3 zoom object.
 */
export function zoomOut( ref, zoom ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );
	const transform = d3.zoomTransform( svg.node() );
	const scale = transform.k;

	// Return early if fully zoomed out.
	if ( scale === mapScaleMin ) {
		return;
	}

	svg
		.transition()
		.duration( transitionDuration )
		.call( zoom.scaleTo, scale / 2 );
}

/**
 * Zoom to a specific county.
 *
 * @param {Object} ref           - SVG ref to zoom in on.
 * @param {Object} zoom          - Zoom object.
 * @param {Object} pathGenerator - Path generator.
 * @param {Object} shape         - Shape to zoom to (ie, country)
 * @param {number} height        - Height of the map, generated by pathGenerator.
 */
export function zoomToCountry( ref, zoom, pathGenerator, shape, height ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );

	const [ [ x0, y0 ], [ x1, y1 ] ] = pathGenerator.bounds( shape );
	const shapeWidth = x1 - x0;
	const shapeHeight = y1 - y0;
	// Calculate the scale to fit the shape into the viewport, use min and max scale limits.
	const scale = Math.max( 1.1, Math.min( mapScaleMax, 0.9 / Math.max( shapeWidth / 960, shapeHeight / 500 ) ) );

	// Position the shape in the left 2/3 of the map.
	let translateX = mapWidth / 3;
	let translateY = height / 2;

	// Position overrides for specific countries with outlying path shapes (ie, Hawaii)
	// U.S.
	if ( shape.id === '840' ) {
		translateX = ( mapWidth / 4 ) - 100;
	}

	// Russia
	if ( shape.id === '643' ) {
		translateX = mapWidth / 8;
		translateY = height / 2.5;
	}

	// Perform the transition on the D3 selection.
	svg
		.transition()
		.duration( transitionDuration )
		.call(
			zoom.transform,
			d3.zoomIdentity
				.translate( ( translateX ), ( translateY ) )
				.scale( scale )
				.translate( -( x0 + x1 ) / 2, -( y0 + y1 ) / 2 ),
		);
}

/**
 * Zoom reset.
 *
 * @param {Object} ref  - SVG reference.
 * @param {Object} zoom - d3 zoom object.
 */
export function zoomReset( ref, zoom ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );

	svg
		.transition()
		.duration( transitionDuration )
		.call(
			zoom.transform,
			d3.zoomIdentity
		);
}
